import React, { type FC, useCallback } from 'react';
import { observer } from '@legendapp/state/react';
import { MarioBottomSheetFlatList } from '../../../../../utils';
import { SelectedGifts } from '../../selected-gifts/selected-gifts';
import type { GiftSection } from '../gifts-selector-widget-contents.types';
import { useGiftSelection } from '../../../hooks/use-gift-selection.hook';
import { useCartItemGiftSelection } from '../../../hooks/use-cart-item-gift-selection.hook';
import { cartItemReferenceStoreAPI } from '@kidslabs/module-quote';

/**
 * Props for CurrentSelectedGiftsComponent
 *
 * @interface CurrentSelectedGiftsComponentProps
 * @property {GiftSection} giftSection - Gift section containing product and available gifts data
 */
export interface CurrentSelectedGiftsComponentProps {
    /**
     * Gift section containing product information and sorted gifts data list
     * Used to display and manage gift selection for the specific product
     */
    giftSection: GiftSection;
}

/**
 * CurrentSelectedGiftsComponent
 *
 * **Purpose:**
 * A container component that displays the list of currently selected gifts within a bottom sheet context.
 * It provides controls to remove gifts or update their quantities based on the parent product's quantity.
 *
 * **Architecture:**
 * - **Container Component**: Handles business logic and state management
 * - **Observable**: Uses Legend-State observer for reactive updates
 * - **Integration**: Connects draft cart store with gift selection logic
 *
 * **Responsibilities:**
 * - Retrieves current product quantity from draft cart store
 * - Initializes gift selection hook with existing session (no new session creation)
 * - Passes selected gifts observable and mutation handlers to presentation component
 * - Manages gift selection state for the bottom sheet context
 *
 * **State Management:**
 * - Uses `cartItemReferenceStoreAPI.state.getQuantityStepperObservable()` for reactive product quantity
 * - Uses `useGiftSelection` hook with `createNewSession: false` to reuse existing session
 * - Passes observable `selectedGifts$` to SelectedGifts for reactive rendering
 *
 * **Data Flow:**
 * 1. Receives `giftSection` prop with product and available gifts
 * 2. Retrieves current product quantity from draft cart store
 * 3. Initializes gift selection hook with product context
 * 4. Passes state and handlers to SelectedGifts presentation component
 * 5. Handles user interactions (remove, update quantity) through hook methods
 *
 * **Integration Points:**
 * - **Draft Cart Store**: Reads product quantity for gift allocation calculation
 * - **Gift Selection Hook**: Manages gift selection business logic
 * - **SelectedGifts Component**: Renders the gift list UI
 * - **MarioBottomSheetFlatList**: Provides bottom sheet optimized list rendering
 *
 * **Usage Context:**
 * Used within GiftsSelectorWidgetContents to display the current gift selection
 * in a bottom sheet when user is adding/editing product with gift promotions.
 *
 * @component
 * @category Gift Management
 * @subcategory Components
 *
 * @example
 * ```tsx
 * // Usage in GiftsSelectorWidgetContents
 * <CurrentSelectedGiftsComponent
 *   giftSection={{
 *     product: selectedProduct,
 *     sortedGiftsDataList: availableGifts
 *   }}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Typical flow:
 * // 1. User opens bottom sheet to select gifts
 * // 2. CurrentSelectedGiftsComponent shows already selected gifts
 * // 3. User can remove gifts or change quantities
 * // 4. Changes are reflected reactively through Legend-State observable
 * ```
 *
 * @remarks
 * - This component uses `createNewSession: false` to avoid creating duplicate gift sessions
 * - It reuses the existing gift selection session from the parent context
 * - The observer wrapper ensures automatic re-rendering when cart quantity changes
 * - Product quantity changes automatically update gift allocation limits
 *
 * @see {@link SelectedGifts} - Presentation component for gift list rendering
 * @see {@link useGiftSelection} - Hook managing gift selection business logic
 * @see {@link cartItemReferenceStoreAPI} - Cart item reference store public API
 * @see {@link GiftSection} - Type definition for gift section data structure
 */
export const CurrentSelectedGiftsComponent: FC<CurrentSelectedGiftsComponentProps> = observer(({ giftSection }) => {
    const sessionId = giftSection.sessionId;
    const { product, sortedGiftsDataList, productQuantity: productQuantityProp, cartItemReference } = giftSection;

    // Determine if we're in cart context (editing existing cart item gifts)
    const isCartContext = !!cartItemReference;

    // Use productQuantity from props if provided (cart context), otherwise use cartItemReferenceStore (PDP context)
    const draftQuantity = cartItemReferenceStoreAPI.state.getQuantityStepperObservable().get();
    const counterValue = productQuantityProp ?? draftQuantity;

    // Use different hooks based on context:
    // - Cart context: useCartItemGiftSelection (uses cartItemReferenceId, initializes from existing selections)
    // - PDP context: useGiftSelection (uses productId-based sessions)
    const pdpGiftHook = useGiftSelection();

    // Get existing session ID for PDP context
    const pdpSessionId = sessionId ? sessionId : (pdpGiftHook.getActiveSessionId(`${product?.id}`, 'product') ?? '');

    const cartGiftSelection = useCartItemGiftSelection({
        cartItemReference: (cartItemReference ?? {
            cartItemReferenceId: '',
            productId: product?.id ?? 0,
            qty: counterValue,
            logics: [],
        }) as unknown as Parameters<typeof useCartItemGiftSelection>[0]['cartItemReference'],
        availableGifts: sortedGiftsDataList,
        sessionId: pdpSessionId,
    });

    const selectedGiftsObservable = isCartContext
        ? cartGiftSelection.store$?.selectedGifts
        : pdpGiftHook.getStoreObservable(pdpSessionId)?.selectedGifts;

    // Create wrapped handlers that match SelectedGifts expected interface
    const handleRemoveGift = useCallback(
        (giftId: string) => {
            if (isCartContext) {
                cartGiftSelection.removeGift(giftId);
            } else {
                pdpGiftHook.removeGift(pdpSessionId, giftId);
            }
        },
        [isCartContext, cartGiftSelection, pdpGiftHook, pdpSessionId]
    );

    const handleUpdateQuantity = useCallback(
        (giftId: string, quantity: number) => {
            if (isCartContext) {
                cartGiftSelection.updateQuantity(giftId, quantity);
            } else {
                // For PDP context, we need to handle quantity update differently
                // since useGiftSelection doesn't have updateQuantity method
                // We can remove and re-add with new quantity, or extend the hook
                console.warn('[CurrentSelectedGiftsComponent] updateQuantity not implemented for PDP context');
            }
        },
        [isCartContext, cartGiftSelection]
    );

    // Guard: Ensure we have a valid observable before rendering
    if (!selectedGiftsObservable) {
        console.warn('[CurrentSelectedGiftsComponent] selectedGiftsObservable is undefined');
        return null;
    }

    return (
        <SelectedGifts
            product={product as unknown as Parameters<typeof SelectedGifts>[0]['product']}
            FlatListComponent={MarioBottomSheetFlatList as Parameters<typeof SelectedGifts>[0]['FlatListComponent']}
            selectedGifts$={selectedGiftsObservable}
            onRemoveGift={handleRemoveGift}
            onUpdateQuantity={handleUpdateQuantity}
            sessionId={pdpSessionId}
        />
    );
});

CurrentSelectedGiftsComponent.displayName = 'CurrentSelectedGiftsComponent';
